import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'
import { MasterCampaignPlanExecutiveSummarySection } from 'api/interfaces/MasterCampaignPlanExecutiveSummarySection'
import { MasterCampaignPlanMediaPlanSection } from 'api/interfaces/MasterCampaignPlanMediaPlanSection'
import { MasterCampaignPlanPersonaSection } from 'api/interfaces/MasterCampaignPlanPersonaSection'
import { MasterCampaignPlanProjectionsSection } from 'api/interfaces/MasterCampaignPlanProjectionsSection'
import { MasterCampaignStub } from 'api/interfaces/MasterCampaignStub'
import { masterCampaignsApi } from 'api/rtkQueryApi/platform/masterCampaignsApi'
import { MasterCampaignStubCampaignBuilderRequest } from 'api/rtkQueryApi/requestModels/MasterCampaignStubCampaignBuilderRequest'

import { MasterCampaign } from '../../interfaces/MasterCampaign'
import { OffAdCritterCustomOption } from '../../interfaces/Organization'
import { defaultTransformFn, platformApi } from './platformApi'

const masterCampaignPlansTags = masterCampaignsApi.enhanceEndpoints({ addTagTypes: ['MasterCampaignPlan', 'AccountMasterCampaignPlans', 'OrganizationMasterCampaignPlans', 'MasterCampaignPlanMasterCampaignStub', 'AccountMasterCampaignStubs', 'MasterCampaignStub', 'MasterCampaignPlanExecutiveSummarySection', 'MasterCampaignPlanMediaPlanSection', 'MasterCampaignPlanPersonaSection', 'MasterCampaignPlanProjectionsSection'] })

type CreateMasterCampaignPlanRequest = {
  organizationId: string
  params: {
    accountId: string
    businessName: string
    masterCampaignPlanName: string
    website: string
    aiRecommendedBudget: boolean
    targetMonthlyBudget: number
    maxSingleBudget?: number
    aiRecommendedStartAndDuration: boolean
    startDate: string
    durationInMonths: number
    details?: string
  }
}

export const masterCampaignPlansApi = masterCampaignPlansTags.injectEndpoints({
  endpoints: build => ({
    beginCreateMasterCampaignPlan: build.mutation<MasterCampaignPlan, CreateMasterCampaignPlanRequest>({
      query: ({ params }) => ({
        url: `masterCampaignPlans`,
        method: 'POST',
        data: {
          ...params
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.params.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    getMasterCampaignPlan: build.query<MasterCampaignPlan, { masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlan', id: request.masterCampaignPlanId }]
    }),
    getMasterCampaignPlansByAccountId: build.query<MasterCampaignPlan[], { accountId: string }>({
      query: ({ accountId }) => ({
        url: `masterCampaignPlans`,
        method: 'GET',
        params: {
          accountId
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AccountMasterCampaignPlans', id: request.accountId }]
    }),
    getMasterCampaignPlans: build.query<MasterCampaignPlan[], { organizationId: string }>({
      query: () => ({
        url: `masterCampaignPlans`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'OrganizationMasterCampaignPlans', id: request.organizationId }]
    }),
    deleteMasterCampaignPlan: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    getMasterCampaignPlanMasterCampaignStub: build.query<MasterCampaignStub, { masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignStubs/${masterCampaignPlanId}/masterCampaignStub`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlanMasterCampaignStub', id: request.masterCampaignPlanId },
        { type: 'MasterCampaignStub', id: result?.id }
      ]
    }),
    getMasterCampaignStubsForAccount: build.query<MasterCampaignStub[], { accountId: string }>({
      query: ({ accountId }) => ({
        url: `masterCampaignStubs/${accountId}/forAccount`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'AccountMasterCampaignStubs', id: request.accountId }]
    }),
    updateInEditMode: build.mutation<MasterCampaignPlan, { masterCampaignPlanId: string; inEditMode: boolean }>({
      query: ({ masterCampaignPlanId, inEditMode }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/updateInEditMode`,
        method: 'POST',
        data: { inEditMode },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ masterCampaignPlanId, inEditMode }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            draft.inEditMode = inEditMode
          })
        )

        return queryFulfilled.then()
      },
      invalidatesTags: result => [
        { type: 'AccountMasterCampaignPlans', id: result?.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: result?.organizationId }
      ]
    }),
    masterCampaignStubReset: build.mutation<MasterCampaignStub, { masterCampaignPlanId: string; masterCampaignStubId: string }>({
      query: ({ masterCampaignStubId }) => ({
        url: `masterCampaignStubs/${masterCampaignStubId}/edit/resetMasterCampaignStub`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ masterCampaignPlanId }, { dispatch, queryFulfilled }) {
        const { data: stub } = await queryFulfilled

        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlanMasterCampaignStub', { masterCampaignPlanId }, (draft: MasterCampaignStub) => {
            Object.assign(draft, stub)
          })
        )
      }
    }),
    addMasterCampaignStubCampaignBuilder: build.mutation<MasterCampaignStub, { masterCampaignStubId: string; request: MasterCampaignStubCampaignBuilderRequest }>({
      query: ({ masterCampaignStubId, request }) => ({
        url: `masterCampaignStubs/${masterCampaignStubId}/edit/addMasterCampaignStubCampaignBuilder`,
        method: 'POST',
        data: request,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [
        { type: 'MasterCampaignPlanMasterCampaignStub', id: _result?.masterCampaignPlanId },
        { type: 'MasterCampaignStub', id: request.masterCampaignStubId }
      ]
    }),
    editMasterCampaignStubCampaignBuilder: build.mutation<MasterCampaignStub, { masterCampaignStubId: string; masterCampaignStubCampaignBuilderId: string; masterCampaignPlanId: string; request: MasterCampaignStubCampaignBuilderRequest }>({
      query: ({ masterCampaignStubId, masterCampaignStubCampaignBuilderId, request }) => ({
        url: `masterCampaignStubs/${masterCampaignStubId}/edit/editMasterCampaignStubCampaignBuilder/${masterCampaignStubCampaignBuilderId}`,
        method: 'POST',
        data: request,
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [
        { type: 'MasterCampaignPlanMasterCampaignStub', id: request.masterCampaignPlanId },
        { type: 'MasterCampaignPlan', id: request.masterCampaignPlanId },
        { type: 'MasterCampaignStub', id: request.masterCampaignStubId }
      ]
    }),
    removeMasterCampaignStubCampaignBuilder: build.mutation<MasterCampaignStub, { masterCampaignPlanId: string; masterCampaignStubId: string; masterCampaignStubCampaignBuilderId: string }>({
      query: ({ masterCampaignStubId, masterCampaignStubCampaignBuilderId }) => ({
        url: `masterCampaignStubs/${masterCampaignStubId}/edit/removeMasterCampaignStubCampaignBuilder/${masterCampaignStubCampaignBuilderId}`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [
        { type: 'MasterCampaignPlanMasterCampaignStub', id: request.masterCampaignPlanId },
        { type: 'MasterCampaignStub', id: request.masterCampaignStubId }
      ]
    }),
    masterCampaignPlanResetAll: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/resetAll`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ masterCampaignPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled

        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    masterCampaignPlanResetPlanDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/resetPlanDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ masterCampaignPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled

        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    masterCampaignPlanEditPlanDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string; planSummary: string; planDetails: string }>({
      query: ({ masterCampaignPlanId, planSummary, planDetails }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/planDetails`,
        method: 'POST',
        data: { summary: planSummary, details: planDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ masterCampaignPlanId, planSummary, planDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            draft.planSummary = planSummary
            draft.planDetails = planDetails
          })
        )

        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    masterCampaignPlanResetAudienceDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/resetAudienceDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ masterCampaignPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled

        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    masterCampaignPlanEditAudienceDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string; audienceSummary: string; audienceDetails: string }>({
      query: ({ masterCampaignPlanId, audienceSummary, audienceDetails }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/audienceDetails`,
        method: 'POST',
        data: { summary: audienceSummary, details: audienceDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ masterCampaignPlanId, audienceSummary, audienceDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            draft.audienceSummary = audienceSummary
            draft.audienceDetails = audienceDetails
          })
        )

        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    masterCampaignPlanResetGeoDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/resetGeoDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ masterCampaignPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled

        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    masterCampaignPlanEditGeoDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string; geoSummary: string; geoDetails: string }>({
      query: ({ masterCampaignPlanId, geoSummary, geoDetails }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/geoDetails`,
        method: 'POST',
        data: { summary: geoSummary, details: geoDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ masterCampaignPlanId, geoSummary, geoDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            draft.geoDetails = geoDetails
            draft.geoSummary = geoSummary
          })
        )

        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    masterCampaignPlanResetSeasonalityDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/resetSeasonalityDetails`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ masterCampaignPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled

        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    masterCampaignPlanEditSeasonalityDetails: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string; seasonalitySummary: string; seasonalityDetails: string }>({
      query: ({ masterCampaignPlanId, seasonalitySummary, seasonalityDetails }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/seasonalityDetails`,
        method: 'POST',
        data: { summary: seasonalitySummary, details: seasonalityDetails },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ masterCampaignPlanId, seasonalitySummary, seasonalityDetails }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            draft.seasonalityDetails = seasonalityDetails
            draft.seasonalitySummary = seasonalitySummary
          })
        )

        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    masterCampaignPlanEditName: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string; name: string }>({
      query: ({ masterCampaignPlanId, name }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/name`,
        method: 'POST',
        data: { name },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ masterCampaignPlanId, name }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            draft.name = name
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    masterCampaignPlanResetPersonas: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/resetPersonas`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      async onQueryStarted({ masterCampaignPlanId }, { dispatch, queryFulfilled }) {
        const { data: plan } = await queryFulfilled

        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            Object.assign(draft, plan)
          })
        )
      }
    }),
    masterCampaignPlanEditPersonas: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string; personas: any[] }>({
      query: ({ masterCampaignPlanId, personas }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/personas`,
        method: 'POST',
        data: { personas },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      onQueryStarted({ masterCampaignPlanId, personas }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignPlansApi.util.updateQueryData('getMasterCampaignPlan', { masterCampaignPlanId }, (draft: MasterCampaignPlan) => {
            draft.personas = personas
          })
        )
        return queryFulfilled.then()
      },
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    masterCampaignPlanRemovePersona: build.mutation<MasterCampaignPlan, { organizationId: string; accountId: string; masterCampaignPlanId: string; personaName: string; personaRank: number }>({
      query: ({ masterCampaignPlanId, personaName, personaRank }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/edit/removePersona/${encodeURIComponent(personaName)}/${personaRank}`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    transferMasterCampaignPlanToDsp: build.mutation<MasterCampaign, { organizationId: string; accountId: string; masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/transferToDsp`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: request.masterCampaignPlanId },
        { type: 'MasterCampaignPlanMasterCampaignStub', id: request.masterCampaignPlanId },
        { type: 'MasterCampaign', id: result?.id },
        { type: 'MasterCampaignCampaigns', id: result?.id },
        { type: 'MasterCampaigns', id: request.accountId },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    createMasterCampaignPlanPublicId: build.mutation<MasterCampaignPlan, { masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/createMasterCampaignPlanPublicId`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: result => [{ type: 'MasterCampaignPlan', id: result?.id }]
    }),
    updateClientPreviewTabs: build.mutation<MasterCampaignPlan, { masterCampaignPlanId: string; showExecutiveSummary: boolean; showMarketAnalysis: boolean; showPersonas: boolean; showMediaPlan: boolean; showProjections: boolean }>({
      query: ({ masterCampaignPlanId, showExecutiveSummary, showMarketAnalysis, showPersonas, showMediaPlan, showProjections }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/clientPreviewTabs`,
        method: 'PUT',
        data: { showExecutiveSummary, showMarketAnalysis, showPersonas, showMediaPlan, showProjections },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: result => [{ type: 'MasterCampaignPlan', id: result?.id }]
    }),
    updateMarketAnalysis: build.mutation<MasterCampaignPlan, { masterCampaignPlanId: string; marketAnalysisContent: string }>({
      query: ({ masterCampaignPlanId, marketAnalysisContent }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/marketAnalysis`,
        method: 'PUT',
        data: { marketAnalysisContent },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: result => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: result?.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: result?.organizationId }
      ]
    }),
    uploadMarketAnalysis: build.mutation<MasterCampaignPlan, { masterCampaignPlanId: string; file: File }>({
      query: ({ masterCampaignPlanId, file }) => {
        const formData = new FormData()
        formData.append('file', file)

        return {
          url: `masterCampaignPlans/${masterCampaignPlanId}/marketAnalysis/upload`,
          method: 'POST',
          data: formData,
          isContext: true,
          isAgency: false
        }
      },
      transformResponse: defaultTransformFn,
      invalidatesTags: result => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: result?.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: result?.organizationId }
      ]
    }),
    transferCampaignToDsp: build.mutation<{ masterCampaignPlanId: string; masterCampaignId: string; dspCampaignId: string; transferStatus: string }, { organizationId: string; accountId: string; masterCampaignPlanId: string; campaignBuilderId: string }>({
      query: ({ masterCampaignPlanId, campaignBuilderId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/campaigns/${campaignBuilderId}/transferToDsp`,
        method: 'POST',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [
        { type: 'MasterCampaignPlan', id: request.masterCampaignPlanId },
        { type: 'MasterCampaignPlanMasterCampaignStub', id: request.masterCampaignPlanId },
        { type: 'MasterCampaignStub', id: request.masterCampaignPlanId },
        { type: 'MasterCampaign', id: result?.masterCampaignId },
        { type: 'MasterCampaignCampaigns', id: result?.masterCampaignId },
        { type: 'MasterCampaigns', id: request.accountId },
        { type: 'AccountMasterCampaignPlans', id: request.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: request.organizationId }
      ]
    }),
    upsertMasterCampaignPlanOffAdCritterCustomOption: build.mutation<MasterCampaignPlan, { masterCampaignPlanId: string; name: string; color: string }>({
      query: ({ masterCampaignPlanId, name, color }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/upsertOffAdCritterCustomOption`,
        method: 'POST',
        data: { name, color },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: result => [
        { type: 'MasterCampaignPlan', id: result?.id },
        { type: 'AccountMasterCampaignPlans', id: result?.accountId },
        { type: 'OrganizationMasterCampaignPlans', id: result?.organizationId }
      ]
    }),
    getMasterCampaignPlanExecutiveSummarySection: build.query<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    updateExecutiveSummaryIntroductionSection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          introduction: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    updateChannelUtilizationSection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          channelUtilization: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    updateAudienceStrategySection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          audienceStrategy: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    updateGeographicTargetingSection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          geographicTargeting: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    updateBudgetSection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          budget: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    hideExecutiveSummarySection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; sectionType: 'introduction' | 'channelUtilization' | 'audienceStrategy' | 'geographicTargeting' | 'budget' }>({
      query: ({ masterCampaignPlanId, sectionType }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    addCustomSection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; position: 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, position, existingSections, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    updateCustomSection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; position: 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    deleteCustomSection: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; position: 'aboveCampaignTimeline' | 'aboveChannelUtilization' | 'aboveAudienceAndGeoSummary' | 'aboveBudget' | 'aboveBottom'; existingSections: any[]; sectionIndex: number }>({
      query: ({ masterCampaignPlanId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    updatePageTitle: build.mutation<MasterCampaignPlanExecutiveSummarySection, { masterCampaignPlanId: string; pageTitle: string }>({
      query: ({ masterCampaignPlanId, pageTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/executiveSummarySection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanExecutiveSummarySection', id: request.masterCampaignPlanId }]
    }),
    getMasterCampaignPlanPersonaSection: build.query<MasterCampaignPlanPersonaSection, { masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/personaSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanPersonaSection', id: request.masterCampaignPlanId }]
    }),
    updatePersonaIntroductionSection: build.mutation<MasterCampaignPlanPersonaSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/personaSection`,
        method: 'PUT',
        data: {
          introduction: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanPersonaSection', id: request.masterCampaignPlanId }]
    }),
    hidePersonaSection: build.mutation<MasterCampaignPlanPersonaSection, { masterCampaignPlanId: string; sectionType: 'introduction' }>({
      query: ({ masterCampaignPlanId, sectionType }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/personaSection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanPersonaSection', id: request.masterCampaignPlanId }]
    }),
    addPersonaCustomSection: build.mutation<MasterCampaignPlanPersonaSection, { masterCampaignPlanId: string; position: 'abovePersonaList'; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, position, existingSections, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/personaSection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanPersonaSection', id: request.masterCampaignPlanId }]
    }),
    updatePersonaCustomSection: build.mutation<MasterCampaignPlanPersonaSection, { masterCampaignPlanId: string; position: 'abovePersonaList'; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `masterCampaignPlans/${masterCampaignPlanId}/personaSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanPersonaSection', id: request.masterCampaignPlanId }]
    }),
    deletePersonaCustomSection: build.mutation<MasterCampaignPlanPersonaSection, { masterCampaignPlanId: string; position: 'abovePersonaList'; existingSections: any[]; sectionIndex: number }>({
      query: ({ masterCampaignPlanId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `masterCampaignPlans/${masterCampaignPlanId}/personaSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanPersonaSection', id: request.masterCampaignPlanId }]
    }),
    updatePersonaPageTitle: build.mutation<MasterCampaignPlanPersonaSection, { masterCampaignPlanId: string; pageTitle: string }>({
      query: ({ masterCampaignPlanId, pageTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/personaSection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanPersonaSection', id: request.masterCampaignPlanId }]
    }),
    getMasterCampaignPlanMediaPlanSection: build.query<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    updateMediaPlanBudgetSection: build.mutation<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          budget: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    updateMediaPlanCampaignDetailsTitle: build.mutation<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string; campaignDetailsTitle: string }>({
      query: ({ masterCampaignPlanId, campaignDetailsTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          campaignDetailsTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    hideMediaPlanSection: build.mutation<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string; sectionType: 'budget' }>({
      query: ({ masterCampaignPlanId, sectionType }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    addMediaPlanCustomSection: build.mutation<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string; position: 'aboveCampaignTimeline' | 'aboveCampaignDetails'; existingSections: any[]; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, position, existingSections, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          [position]: [...existingSections, { title, content, showTitle }]
        },
        isContext: true,
        isAgency: false
      }),
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    updateMediaPlanCustomSection: build.mutation<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string; position: 'aboveCampaignTimeline' | 'aboveCampaignDetails'; existingSections: any[]; sectionIndex: number; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, position, existingSections, sectionIndex, title, content, showTitle }) => {
        const updatedSections = [...existingSections]
        updatedSections[sectionIndex] = { ...updatedSections[sectionIndex], title, content, showTitle }
        return {
          url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    deleteMediaPlanCustomSection: build.mutation<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string; position: 'aboveCampaignTimeline' | 'aboveCampaignDetails'; existingSections: any[]; sectionIndex: number }>({
      query: ({ masterCampaignPlanId, position, existingSections, sectionIndex }) => {
        const updatedSections = [...existingSections]
        updatedSections.splice(sectionIndex, 1)
        return {
          url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
          method: 'PUT',
          data: {
            [position]: updatedSections
          },
          isContext: true,
          isAgency: false
        }
      },
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    updateMediaPlanPageTitle: build.mutation<MasterCampaignPlanMediaPlanSection, { masterCampaignPlanId: string; pageTitle: string }>({
      query: ({ masterCampaignPlanId, pageTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/mediaPlanSection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanMediaPlanSection', id: request.masterCampaignPlanId }]
    }),
    getMasterCampaignPlanProjectionsSection: build.query<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string }>({
      query: ({ masterCampaignPlanId }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsCpmAndImpressionsSection: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          cpmAndImpressions: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsAuctionBasedPlacementsSection: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          auctionBasedPlacements: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsCtvPlacementCategoriesSection: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          ctvPlacementCategories: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsInternetAdPlacementsSection: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          internetAdPlacements: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsGeographicDistributionSection: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          geographicDistribution: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsBillboardLocationsSection: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; title: string; content: string; showTitle: boolean }>({
      query: ({ masterCampaignPlanId, title, content, showTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          billboardLocations: { title, content, showTitle }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    hideProjectionsSection: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; sectionType: 'cpmAndImpressions' | 'auctionBasedPlacements' | 'ctvPlacementCategories' | 'internetAdPlacements' | 'geographicDistribution' | 'billboardLocations' }>({
      query: ({ masterCampaignPlanId, sectionType }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          [sectionType]: { hideAll: true }
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsPageTitle: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; pageTitle: string }>({
      query: ({ masterCampaignPlanId, pageTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          pageTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    }),
    updateProjectionsWhereAdsAppearTitle: build.mutation<MasterCampaignPlanProjectionsSection, { masterCampaignPlanId: string; whereAdsAppearTitle: string }>({
      query: ({ masterCampaignPlanId, whereAdsAppearTitle }) => ({
        url: `masterCampaignPlans/${masterCampaignPlanId}/projectionsSection`,
        method: 'PUT',
        data: {
          whereAdsAppearTitle
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, request) => [{ type: 'MasterCampaignPlanProjectionsSection', id: request.masterCampaignPlanId }]
    })
  })
})

export const {
  useBeginCreateMasterCampaignPlanMutation,
  useGetMasterCampaignPlanQuery,
  useGetMasterCampaignPlansByAccountIdQuery,
  useGetMasterCampaignPlansQuery,
  useDeleteMasterCampaignPlanMutation,
  useGetMasterCampaignPlanMasterCampaignStubQuery,
  useGetMasterCampaignStubsForAccountQuery,
  useTransferMasterCampaignPlanToDspMutation,
  useUpdateInEditModeMutation,
  useMasterCampaignStubResetMutation,
  useAddMasterCampaignStubCampaignBuilderMutation,
  useEditMasterCampaignStubCampaignBuilderMutation,
  useRemoveMasterCampaignStubCampaignBuilderMutation,
  useMasterCampaignPlanResetAllMutation,
  useMasterCampaignPlanResetPlanDetailsMutation,
  useMasterCampaignPlanEditPlanDetailsMutation,
  useMasterCampaignPlanResetAudienceDetailsMutation,
  useMasterCampaignPlanEditAudienceDetailsMutation,
  useMasterCampaignPlanResetGeoDetailsMutation,
  useMasterCampaignPlanEditGeoDetailsMutation,
  useMasterCampaignPlanResetSeasonalityDetailsMutation,
  useMasterCampaignPlanEditSeasonalityDetailsMutation,
  useMasterCampaignPlanEditNameMutation,
  useMasterCampaignPlanResetPersonasMutation,
  useMasterCampaignPlanEditPersonasMutation,
  useMasterCampaignPlanRemovePersonaMutation,
  useCreateMasterCampaignPlanPublicIdMutation,
  useUpdateClientPreviewTabsMutation,
  useUpdateMarketAnalysisMutation,
  useUploadMarketAnalysisMutation,
  useTransferCampaignToDspMutation,
  useUpsertMasterCampaignPlanOffAdCritterCustomOptionMutation,
  useGetMasterCampaignPlanExecutiveSummarySectionQuery,
  useUpdateExecutiveSummaryIntroductionSectionMutation,
  useUpdateChannelUtilizationSectionMutation,
  useUpdateAudienceStrategySectionMutation,
  useUpdateGeographicTargetingSectionMutation,
  useUpdateBudgetSectionMutation,
  useHideExecutiveSummarySectionMutation,
  useAddCustomSectionMutation,
  useUpdateCustomSectionMutation,
  useDeleteCustomSectionMutation,
  useUpdatePageTitleMutation,
  useGetMasterCampaignPlanPersonaSectionQuery,
  useUpdatePersonaIntroductionSectionMutation,
  useHidePersonaSectionMutation,
  useAddPersonaCustomSectionMutation,
  useUpdatePersonaCustomSectionMutation,
  useDeletePersonaCustomSectionMutation,
  useUpdatePersonaPageTitleMutation,
  useGetMasterCampaignPlanMediaPlanSectionQuery,
  useUpdateMediaPlanBudgetSectionMutation,
  useUpdateMediaPlanCampaignDetailsTitleMutation,
  useHideMediaPlanSectionMutation,
  useAddMediaPlanCustomSectionMutation,
  useUpdateMediaPlanCustomSectionMutation,
  useDeleteMediaPlanCustomSectionMutation,
  useUpdateMediaPlanPageTitleMutation,
  useGetMasterCampaignPlanProjectionsSectionQuery,
  useUpdateProjectionsCpmAndImpressionsSectionMutation,
  useUpdateProjectionsAuctionBasedPlacementsSectionMutation,
  useUpdateProjectionsCtvPlacementCategoriesSectionMutation,
  useUpdateProjectionsInternetAdPlacementsSectionMutation,
  useUpdateProjectionsGeographicDistributionSectionMutation,
  useUpdateProjectionsBillboardLocationsSectionMutation,
  useHideProjectionsSectionMutation,
  useUpdateProjectionsPageTitleMutation,
  useUpdateProjectionsWhereAdsAppearTitleMutation
} = masterCampaignPlansApi
